/**
 * The example budget sheet behind "See an example" on the Budgets tab.
 *
 * Read-only: it is never stored, and "Make a copy to edit" deep-copies it
 * into the person's own sheets. Round numbers on purpose, so each worked-out
 * row can be checked in your head, and every kind of row appears at least
 * once:
 *   typed            Paycheck 1, Rent, Savings...
 *   add these up     Housing (under the Bills total line)
 *   what's left      After bills, Spending money, Everyday account
 *   a share of       Investing (10%), and half of each plan amount per payday
 *   not in the total Housing
 *   notes            several
 * "Each payday" is the plan split in half - bills, savings and investing each
 * take half their monthly amount - and adds up to exactly one paycheck, so
 * what's left for the everyday account comes out at exactly half the monthly
 * spending money. No side income on purpose: it isn't part of either
 * paycheck, and it would stop those two numbers matching.
 */

export const EXAMPLE_ID = '__example__';

const row = (id, name, amount, extra = {}) => ({
  id, name, amount, note: '', below: false, calc: null, ...extra,
});

export const EXAMPLE_SHEET = {
  id: EXAMPLE_ID,
  name: 'Example: monthly plan',
  emoji: '🗓️',
  pinned: false,
  sections: [
    {
      id: 'inc', title: 'Income', showTotal: true,
      rows: [
        row('p1', 'Paycheck 1', 2400),
        row('p2', 'Paycheck 2', 2400),
      ],
    },
    {
      id: 'bills', title: 'Bills', showTotal: true,
      rows: [
        row('rent', 'Rent', 1650),
        row('util', 'Utilities', 120),
        row('net', 'Internet', 60),
        row('phone', 'Phone', 65),
        row('car', 'Car payment', 380),
        row('ins', 'Insurance', 140, { note: 'Car and tenant' }),
        row('subs', 'Subscriptions', 45),
        row('housing', 'Housing', 0, {
          below: true,
          note: 'Just for reference - not added into the total',
          calc: { type: 'sum', refs: ['row:rent', 'row:util', 'row:net'] },
        }),
      ],
    },
    {
      id: 'plan', title: 'My monthly plan', showTotal: false,
      rows: [
        row('after', 'After bills', 0, {
          calc: { type: 'left', from: 'sec:inc', minus: ['sec:bills'] },
        }),
        row('save', 'Savings', 600, { note: 'High-interest savings account' }),
        row('invest', 'Investing', 0, {
          calc: { type: 'share', of: 'sec:inc', part: 'pct', pct: 10 },
        }),
        row('spend', 'Spending money', 0, {
          note: 'Groceries, going out, everything else',
          calc: { type: 'left', from: 'row:after', minus: ['row:save', 'row:invest'] },
        }),
      ],
    },
    {
      id: 'payday', title: 'Each biweekly payday', showTotal: true,
      rows: [
        row('billsacct', 'Bills account', 0, {
          note: 'Half the bills, because there are two paydays',
          calc: { type: 'share', of: 'sec:bills', part: 'half' },
        }),
        row('psave', 'Savings', 0, {
          calc: { type: 'share', of: 'row:save', part: 'half' },
        }),
        row('pinvest', 'Investing', 0, {
          calc: { type: 'share', of: 'row:invest', part: 'half' },
        }),
        row('everyday', 'Everyday account', 0, {
          note: 'What\u2019s left of the paycheck, half my spending money',
          calc: { type: 'left', from: 'row:p1', minus: ['row:billsacct', 'row:psave', 'row:pinvest'] },
        }),
      ],
    },
  ],
};
